"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Offerings() {
  const intro = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    id: "01",
    category: "STRATEGY",
    title: "SEO & AEO",
    description: "Optimizing for the modern search landscape. We ensure your brand is the definitive answer for both traditional search engines and AI-driven discovery platforms.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/seo_aeo_strategy_minimal.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/seo_aeo_strategy_minimal.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/seo_aeo_strategy_minimal.webp-wvc-sizes",
    alt: "Abstract architectural lines representing structure and flow"
  }, {
    id: "02",
    category: "VISIBILITY",
    title: "AI Visibility",
    description: "The future of brand discovery is generative. We position your business to be cited, recommended, and prioritized by LLMs and AI assistants.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/ai_visibility_consulting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/ai_visibility_consulting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/ai_visibility_consulting.png-wvc-sizes",
    alt: "Soft light reflecting off a metallic surface"
  }, {
    id: "03",
    category: "CONTENT",
    title: "Podcast Production",
    description: "High-fidelity storytelling that builds deep authority. From conceptualization to post-production, we create audio experiences that command attention.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/podcast_production_editorial.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/podcast_production_editorial.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/podcast_production_editorial.webp-wvc-sizes",
    alt: "Minimalist studio setup with soft shadows"
  }, {
    id: "04",
    category: "IDENTITY",
    title: "Brand Guidelines",
    description: "A cohesive visual and verbal language. We define the rules of your brand's universe to ensure every touchpoint resonates with intentionality.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_guidelines_design.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_guidelines_design.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/brand_guidelines_design.webp-wvc-sizes",
    alt: "Clean paper textures and sharp typography"
  }];
  return <section data-section-id="2683" id="strategic-offerings" className="bg-background text-foreground py-32 md:py-48 px-6 md:px-12 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div ref={intro.ref} className={`mb-32 md:mb-48 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12">
            <div className="max-w-5xl">
              <p className="font-mono text-[12px] font-medium uppercase tracking-[0.25em] text-accent mb-8">
                Our Discipline
              </p>
              <h2 className="font-default font-medium text-5xl md:text-8xl lg:text-[7.5rem] leading-[0.9] tracking-[0.15em] text-foreground uppercase">
                Strategic <br />
                <span className="text-foreground/60">Solutions.</span>
              </h2>
            </div>
            <div className="max-w-[380px] lg:pb-6">
              <p className="text-lg md:text-xl text-foreground/80 leading-relaxed font-light">
                A curated suite of services engineered to bridge the gap between your vision and the digital world.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-32 md:gap-48">
          {services.map((service, i) => <ServiceItem key={i} data-index={i} service={service} isReversed={i % 2 === 1} />)}
        </div>

        <div className="mt-32 md:mt-48 flex justify-center">
          <Button asChild variant="outline" className="rounded-lg border-foreground/20 text-foreground px-12 py-8 h-auto text-xs uppercase tracking-[0.25em] hover:bg-foreground hover:text-background transition-all duration-500 shadow">
            <Link to="/ServicesPage">
              View All Services
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}
function ServiceItem({
  service,
  isReversed,
  ...rest
}: {
  service: {
    id: string;
    category: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  isReversed: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} {...rest} className={`relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      <div className={`flex flex-col lg:flex-row gap-12 md:gap-24 items-center ${isReversed ? "lg:flex-row-reverse" : ""}`}>
        {}
        <div className="w-full lg:w-7/12">
          <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted shadow">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 ease-out hover:scale-105" />
          </div>
        </div>

        {}
        <div className="w-full lg:w-5/12 flex flex-col">
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-[12px] tracking-[0.25em] text-accent">
              {service.category}
            </span>
            <div className="h-px flex-1 bg-foreground/10" />
            <span className="font-mono text-[12px] text-foreground/40">
              {service.id}
            </span>
          </div>

          <h3 className="font-default font-medium text-4xl md:text-5xl leading-[1.1] tracking-[0.1em] text-foreground uppercase mb-8">
            {service.title}
          </h3>

          <p className="text-lg text-foreground/70 leading-relaxed mb-12 max-w-md font-light">
            {service.description}
          </p>

          <div className="pt-8 border-t border-foreground/10">
            <Button asChild variant="default" className="rounded-lg bg-accent text-accent-foreground px-8 py-6 h-auto text-xs uppercase tracking-[0.25em] hover:opacity-90 transition-all duration-500 shadow">
              <Link to="/ContactPage">
                <span>Start Project</span>
                <ArrowUpRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}